import { useEffect, useState } from "react";

import Loading from "@/components/ui/loading";
import { Button, Modal, Table } from "react-bootstrap";

import api from "@/lib/api";

type ModuleModalProps = {
	moduleId: number;
	showModuleModal: boolean;
	handleCloseModuleModal: () => void;
	handleOpenItemModal: (
		itemId: number,
		items: ItemProps[],
		currentIndex: number
	) => void;
	setSurveyModal: React.Dispatch<React.SetStateAction<boolean>>;
	setModuleModal: React.Dispatch<React.SetStateAction<boolean>>;
	setSurveyId: React.Dispatch<React.SetStateAction<number | undefined>>;
};

type ItemProps = {
	id: number;
	title: string;
	description: string;
	item_type: {
		title: string;
	};
};

type ModuleModalData = {
	overview: {
		id: number;
		title: string;
		study_title: string;
	};
	module: {
		title: string;
		description: string;
		survey: {
			value: string;
			label: string;
			overview_id: string;
		};
	};
	items: ItemProps[];
};

export async function getModuleModalData(module_id: number) {
	try {
		const response = await api.get<ModuleModalData>(`/srv/module/${module_id}`);
		return response;
	} catch (error) {
		console.error("Error fetching module data:", error);
		throw new Error("Failed to fetch module data");
	}
}

export default function ModuleModal({
	showModuleModal,
	moduleId,
	handleCloseModuleModal,
	handleOpenItemModal,
	setSurveyModal,
	setModuleModal,
	setSurveyId,
}: ModuleModalProps) {
	const [moduleData, setModuleData] = useState<ModuleModalData>();
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		const fetchData = async () => {
			if (moduleId) {
				setLoading(true);
				const response = await getModuleModalData(moduleId);
				setModuleData(response);
				if (response.module.survey.overview_id != null) {
					setSurveyId(Number(response.module.survey.overview_id));
				} else {
					setSurveyId(undefined);
				}
				setLoading(false);
			}
		};
		fetchData();
	}, [showModuleModal, moduleId]);

	const totalItems = moduleData?.items.length || 0;

	return (
		<Modal
			show={showModuleModal}
			onHide={handleCloseModuleModal}
			size="lg"
			aria-labelledby="module-item-modal"
		>
			<Modal.Header closeButton>
				<Modal.Title className="g2-text-grey">
					{moduleData?.module.title}
				</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				{loading ? (
					<Loading />
				) : (
					<>
						<p>
							<span
								className="g2-link-blue"
								onClick={() => {
									setModuleModal(false);
									setSurveyModal(true);
								}}
							>
								{moduleData?.overview.study_title} {moduleData?.overview.title}
							</span>{" "}
							&gt;{" "}
							<span className="g2-text-grey">{moduleData?.module.title}</span>
						</p>
						<p className="g2-text-blue fw-bold">Items ({totalItems})</p>
						<Table
							className="table table-striped table-hover table-bordered"
							width="100%"
						>
							<thead className="table-dark">
								<tr className="g2-text-white fw-bold">
									<th>LABEL</th>
									<th>TYPE</th>
									<th>DESCRIPTION</th>
								</tr>
							</thead>
							<tbody>
								{moduleData?.items.map((item, index) => (
									<tr key={index}>
										<td>
											<span
												className="g2-link-blue"
												onClick={() => {
													handleOpenItemModal(item.id, moduleData.items, index);
													handleCloseModuleModal();
												}}
											>
												{item.title}
											</span>
										</td>
										<td>{item.title}</td>
										<td>{item.description}</td>
									</tr>
								))}
							</tbody>
						</Table>
					</>
				)}
			</Modal.Body>
			<Modal.Footer>
				<Button
					className="g2-btn-blue"
					onClick={() => {
						setModuleModal(false);
						setSurveyModal(true);
					}}
				>
					Back to Modules
				</Button>
				<Button variant="secondary" onClick={handleCloseModuleModal}>
					Close
				</Button>
			</Modal.Footer>
		</Modal>
	);
}
